import {
  applyQueryConfig,
  responseStringTransformer,
  serializeStandardResponse,
  ServiceHandler,
} from '../utils/api.utils';
import { standardQuerySerializer, type PageableResponse } from './serializers';

import {
  quoteSearchV2Mappings,
  quoteServiceMappings,
} from '../inbox/quoteServiceMappings';

import {
  pullFactorsFromCowbellRiskFactors,
  serializeCowbellFactors,
} from './serializers/cowbell-factors.serializers';
import { isPrimeX, ProductTypes, type ProductType } from '../types';
import { transformQuoteDetailsResponse } from './serializers/quote.serializers';
import type {
  CheckStatusOnAccountResponse,
  DeleteQuoteFromCartResponse,
  DynamicFields,
  FetchUWInboxStatsInfoResponse,
  AddQuoteToCartResponse,
  GetPrimeXQuoteDetailsResponse,
  GetPxRaterResponse,
  GetQuoteCartListResponse,
  GetQuoteProgress,
  UpdateQuoteCartListPayload,
} from './quotes.api.types';
import {
  PERSONA_TYPES,
  type Persona,
} from '../console/_statics/persona.statics';
import type { QuoteSearchRowDto } from '../types/quotes/tables/quote-table-row.dto';
import {
  serializeLegacyIndustryCode,
  serializePxQuoteIndustryCode,
} from './serializers/industry-code.serializers';

type SearchQuotesResponse = PageableResponse<QuoteSearchRowDto>;
export const searchQuotes = ServiceHandler<unknown, SearchQuotesResponse>({
  url: '/api/quote/v1/search',
  paramsSerializer: (params) =>
    standardQuerySerializer(quoteSearchQuerySerializer(params)),
});

export const getP250QuotesDelta = ServiceHandler({
  url: 'api/quote/v1/P250/delta',
  method: 'POST',
});

/* specific serializers */
function quoteSearchQuerySerializer(params: any) {
  return applyQueryConfig(quoteServiceMappings, params);
}

export const getQuoteP100 = ServiceHandler({
  url: '/api/quote/v1/:quoteId',
  transformResponse: [
    responseStringTransformer,
    pullFactorsFromCowbellRiskFactors,
    serializeCowbellFactors,
    serializeLegacyIndustryCode,
  ],
});

export const getQuoteP250 = ServiceHandler({
  url: '/api/quote/v1/P250/quotes/:quoteId',
  transformResponse: [
    responseStringTransformer,
    pullFactorsFromCowbellRiskFactors,
    serializeCowbellFactors,
    serializeLegacyIndustryCode,
  ],
});

export const getQuoteDetailsP250 = ServiceHandler({
  url: '/api/quote/v1/P250/details/:quoteId',
  transformResponse: [
    responseStringTransformer,
    pullFactorsFromCowbellRiskFactors,
    serializeCowbellFactors,
  ],
});

export const getComparableAccounts = ServiceHandler({
  url: '/api/quote/v1/P250/comparable-accounts/:accountId?productType=P250',
});

export const getStepperQuoteDetails = ServiceHandler({
  url: '/api/quote/v1/:productType/:quoteId?view=full',
  transformResponse: [serializeStandardResponse, serializePxQuoteIndustryCode],
});

export const getPrimeXQuoteDetails = ServiceHandler({
  url: '/api/quote/v1/:productType/:quoteId',
  transformResponse: [serializeStandardResponse, serializePxQuoteIndustryCode],
});

export const getPrimeXAccordionQuoteDetails = ServiceHandler<
  unknown,
  GetPrimeXQuoteDetailsResponse
>({
  url: '/api/quote/v1/:productType/:quoteId?view=full',
  transformResponse: [
    serializeStandardResponse,
    transformQuoteDetailsResponse,
    pullFactorsFromCowbellRiskFactors,
    serializeCowbellFactors,
    serializePxQuoteIndustryCode,
  ],
});

export const getQuoteDetails = (id: string, product: string) => {
  switch (product) {
    case ProductTypes.p100:
      return getQuoteP100(id);
    case ProductTypes.p250:
      return getQuoteDetailsP250(id);
    default:
      return getPrimeXAccordionQuoteDetails(product, id);
  }
};

export const saveQuoteSurplusData = ServiceHandler({
  url: '/api/quote/v1/action/update/surplusData',
  method: 'POST',
});

export const getQuoteProgress = ServiceHandler<unknown, GetQuoteProgress>({
  url: '/api/quote/v1/:quoteId/progress',
});

export const getQuoteSubjectivityProgress = ServiceHandler({
  url: '/api/quote/v1/:productType/quote-subjectivity/:quoteSubjectivityId',
});

export const completeQuoteSubjectivity = ServiceHandler({
  url: '/api/quote/v1/:productType/quote-subjectivity/:quoteSubjectivityId/complete',
  method: 'POST',
});

export type IssuePolicyPersonas = Extract<Persona, 'AGENCY' | 'COWBELL'>;

export const issuePolicy = ({
  persona,
  productType,
  quoteId,
}: {
  persona: IssuePolicyPersonas;
  productType: ProductType;
  quoteId: string;
}) => {
  if (isPrimeX(productType) || persona === PERSONA_TYPES.COWBELL) {
    return primeXIssuePolicy(productType, quoteId);
  }

  return agentIssuePolicy(quoteId);
};

export const primeXIssuePolicy = ServiceHandler({
  url: '/api/quote/v1/:productType/issue/:quoteId',
  method: 'PUT',
});

export const agentIssuePolicy = ServiceHandler({
  url: '/api/quote/v1/:quoteId/agent-issue',
  method: 'PUT',
});

export const createManuscriptSubjectivity = ServiceHandler({
  url: '/api/quote/v1/subjectivity/manuscript',
  method: 'POST',
});

export const changeQuoteStatusToReferred = ServiceHandler({
  url: '/api/quote/v1/:productType/uw/refer/:quoteId',
  method: 'PUT',
});

export const downloadSpecimenDoc = ServiceHandler({
  url: '/api/docs/v1/downloadSpecimen',
});

export const previewDraftProposal = ServiceHandler<
  unknown,
  { previewLink: string }
>({
  url: '/api/quote/v1/draft/quote/:quoteId/proposal/preview',
  method: 'GET',
});

export const previewDraftApplication = ServiceHandler<
  unknown,
  { previewLink: string }
>({
  url: '/api/quote/v1/draft/quote/:quoteId/application/preview',
  method: 'GET',
});

export const previewDraftSpecimen = ServiceHandler<
  unknown,
  { previewLink: string }
>({
  url: '/api/quote/v1/draft/quote/:quoteId/specimen/preview',
  method: 'GET',
});

export const fetchUWInboxStatsInfo = ServiceHandler<
  unknown,
  FetchUWInboxStatsInfoResponse
>({
  url: '/api/quote/v1/search/uw-inbox/aggregate',
  paramsSerializer: (params) =>
    standardQuerySerializer(quoteSearchQuerySerializer(params)),
});

export const checkStatusOnAccount = ServiceHandler<
  unknown,
  CheckStatusOnAccountResponse
>({
  url: '/api/policy/v1/business/:accountId',
});

export const getQuoteCartList = ServiceHandler<
  unknown,
  GetQuoteCartListResponse
>({
  url: '/api/quote/v1/:productType/cart/account/:accountId',
});

export const updateQuoteCartList = ServiceHandler<
  UpdateQuoteCartListPayload,
  AddQuoteToCartResponse
>({
  url: '/api/quote/v1/:productType/cart/account/:accountId',
  method: 'POST',
});

export const reviveQuote = ServiceHandler({
  url: '/api/quote/v1/da/P250/revive/:quoteId',
  method: 'PUT',
});

export const deleteQuoteFromCartList = ServiceHandler<
  unknown,
  DeleteQuoteFromCartResponse
>({
  url: '/api/quote/v1/:productType/cart/:quoteId',
  method: 'DELETE',
});

export const getRaterCalculator = ServiceHandler<any, GetPxRaterResponse>({
  url: '/api/rater/v1/:productType/calculator',
  method: 'POST',
});

export const bulkRequestQuotesFromCart = ServiceHandler({
  url: '/api/quote/v1/:productType/cart/account/:accountId/submit',
  method: 'POST',
});

export const bulkApplyP100QuoteToAccounts = ServiceHandler({
  url: '/api/quote/v1/bulk/quoteAccounts/:quoteId',
  method: 'POST',
});

export interface ValidateAccountProfileResponse {
  pass: boolean;
  errorMessage?: string;
}

export const bulkApplyP250QuoteToAccounts = ServiceHandler({
  url: '/api/quote/v1/P250/bulk/quoteAccounts/:quoteId',
  method: 'POST',
});

export const redoRequestQuote = ServiceHandler<unknown, unknown>({
  url: 'api/quote/v1/:productType/retry/:quoteId',
  method: 'PUT',
});

export const searchQuotesV2 = ServiceHandler({
  url: '/api/quote/v1/search/dashboard',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(quoteSearchV2Mappings, params)),
});

export type UpdatePrimeXQuotePayload = Partial<
  {
    effectiveDate: string;
    endDate: string;
    aggregateLimit: number;
    deductible: number;
    waitingPeriod: number | null;
    retroactivePeriod?: number;
    isNotificationEnabled: boolean;
    retroActiveDate?: string;
    commissionRate: number;
    strictMode?: boolean;
  } & (
    | { coverages: any[]; dynamicFields: never }
    | { coverages: never; dynamicFields: DynamicFields }
  )
>;

export const updatePrimeXQuote = ServiceHandler<UpdatePrimeXQuotePayload>({
  url: '/api/quote/v1/:productType/edit/:quoteId',
  method: 'PUT',
});

interface UpdateQuoteCommissionPayload {
  commission: number;
  quoteId: string;
}

export const updateQuoteCommission =
  ServiceHandler<UpdateQuoteCommissionPayload>({
    url: '/api/quote/v1/action/commission/:quoteId',
    method: 'PUT',
  });

export const fetchUniqueAccountStats = ServiceHandler({
  url: '/api/quote/v1/search/uniqueAccounts',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(quoteServiceMappings, params)),
});

interface FetchPresetCoverageGroupsResponse {
  presetCoverageGroups: {
    name: string;
    groupName: string;
    order: number;
  }[];
}

export const fetchPresetCoverageGroups = ServiceHandler<
  unknown,
  FetchPresetCoverageGroupsResponse
>({
  url: '/api/rater/v1/presets/coverage-groups',
});

export const recalculateQuotePX = ServiceHandler({
  url: '/api/quote/v1/:productType/recalculate/:quoteId',
  method: 'POST',
});

export const submitPxQuoteForUWReview = ServiceHandler<
  Record<'reason', string>,
  unknown
>({
  url: '/api/quote/v1/:productType/request-review/:quoteId',
  method: 'PUT',
});

export const underwritePxQuote = ServiceHandler({
  url: '/api/quote/v1/:productType/uw/underwrite/:quoteId',
  method: 'PUT',
});

export type CarrierRuleName =
  | 'DATE'
  | 'STATE'
  | 'REVENUE'
  | 'CAPACITY'
  | 'ADJACENCY'
  | 'LIMIT';

export interface CarrierRuleOutcome {
  readonly rule: CarrierRuleName;
  readonly outcome: 'PASS' | 'FAIL';
  readonly reason: string | null;
}

// The per-carrier eligibility contract from fir-account (FPX-7027). applicable =
// every rule passed; overridable = fails only revenue; neither = fails a hard rule.
export interface CarrierEligibility {
  readonly treatyId: string;
  readonly carrierName: string;
  readonly policyPrefix: string;
  readonly isAssigned: boolean;
  readonly applicable: boolean;
  readonly overridable: boolean;
  readonly rules: CarrierRuleOutcome[];
}

export interface GetCarrierEligibilityPayload {
  readonly quoteId: string;
  // The in-flight tower (dynamic-fields Value); the server resolves the aggregate
  // limit and tower exposure from it and derives revenue/state by quoteId.
  readonly policyValue: unknown;
  readonly effectiveDate: string;
}

export interface GetCarrierEligibilityResponse {
  readonly carriers: CarrierEligibility[];
}

export const getCarrierEligibility = ServiceHandler<
  GetCarrierEligibilityPayload,
  GetCarrierEligibilityResponse
>({
  url: '/api/quote/v1/:productType/carrier/eligibility',
  method: 'POST',
});

export const getEligibleProducts = ServiceHandler({
  url: '/api/account/v1/account/eligible-products',
});

export const toggleUnderwriterAction = ServiceHandler({
  url: '/api/quote/v1/:productType/uw/inbox/toggle-flag/:quoteId',
  method: 'PUT',
});

export const deletePxQuote = ServiceHandler({
  url: '/api/quote/v1/:productType/:quoteId',
  method: 'DELETE',
});

export const bypassCowbellFactorRules = ServiceHandler({
  url: 'api/quote/v1/:productType/uw/bypass-cf-rules/:quoteId',
  method: 'PUT',
});

export interface UpdateAgencyBilledPayload {
  agencyBilled: boolean;
  dryRun: boolean;
}

export interface UpdateAgencyBilledResponse {
  quoteId: string;
  agencyBilled: boolean;
  dryRun: boolean;
  oldTotalPremium: number | null;
  newTotalPremium: number | null;
  premiumDelta: number | null;
}

export const updateAgencyBilled = ServiceHandler<
  UpdateAgencyBilledPayload,
  UpdateAgencyBilledResponse
>({
  url: 'api/quote/v1/:productType/uw/agency-billed/:quoteId',
  method: 'PUT',
});

interface AssignUWToQuotePayload {
  uwEmail: string;
  uwFullName: string;
}

export const assignUWToQuote = ServiceHandler<AssignUWToQuotePayload, unknown>({
  url: '/api/quote/v1/:productType/:quoteId/underwriter',
  method: 'PUT',
});
